import React from 'react'
import { useSelector } from 'react-redux'
import props from '../../../../../redux/props'
import { formatNumber, roundUp } from '../../../../../utility'
import { Concept, ModuleSizes } from '../../../../../types/Survey/Analysis/Concept'
import {
  PrebuildEinzelarbeitSubType,
  PrebuildMeetingGroesseBesprechungSubType,
  PrebuildMeetingGroesseCoworkingSubType,
  PrebuildType,
} from '../../../../../types/Survey/Element'
import { genericQMValues } from '../../../../hooks/useAnalysisCalculation/getFactorValues'
import Item from './Item'
import { ProductionDistance } from '../../../../../types/OrganizationUnit'
import getSums from '../ConceptCalculation/OrganizationUnit/SurveyResults/getSums'
import TableItem, { TableItemRow } from './TableItem'
type Props = {
  size: ModuleSizes
  qmSize: number
  values?: ({ label: string } & { practicalSpaceAmount: number; practicalQM: number; arbeitsplatzAmount: number })[]
  isSquareMeter?: boolean
  titleSuffix?: string
}

const Composer: React.FC<Props> = ({ size, qmSize, values, titleSuffix }) => {
  const t = useSelector((s) => s[props.TRANSLATION])

  const getTotalAmount = () => {
    return values.reduce((acc, n) => (acc += n.practicalSpaceAmount), 0)
  }

  const getTotalArbeitsplaetze = () => {
    return values.reduce((acc, n) => (acc += n.arbeitsplatzAmount), 0)
  }

  const getTotalQM = () => {
    return values.reduce((acc, n) => (acc += n.practicalQM), 0)
  }

  return (
    <TableItem
      title={`${t.survey.analysis.calculation.values.unitSurveyResultsModules.sizes[size]} (${qmSize}${t.generic.squareMeterSuffix}${
        !!titleSuffix ? ` - ${titleSuffix}` : ''
      })`}
      value={getTotalAmount()}
      suffix={`${t.generic.amountSuffix} - ${getTotalArbeitsplaetze()}${
        t.survey.analysis.concept.summary.modules.arbeitsplaetzeSuffix
      } - ${formatNumber(getTotalQM())}${t.generic.squareMeterSuffix}`}
      doRound
      items={values.map((v) => {
        return (
          <TableItemRow
            key={v.label}
            title={v.label}
            values={[
              { value: v.practicalSpaceAmount, suffix: t.generic.amountSuffix },
              { value: roundUp(v.arbeitsplatzAmount), suffix: t.survey.analysis.concept.summary.modules.arbeitsplaetzeSuffix },
              { value: v.practicalQM, suffix: t.generic.squareMeterSuffix },
            ]}
          />
        )
      })}
    />
  )
}

const Modules: React.FC<{ concept: Concept; productionDistance?: ProductionDistance; floorUUID?: string }> = ({
  concept,
  productionDistance,
  floorUUID,
}) => {
  const t = useSelector((s) => s[props.TRANSLATION])
  const sums = getSums(
    productionDistance
      ? concept.units.sum.productionDistance[productionDistance]
      : floorUUID
      ? concept.units.sum.floor[floorUUID]
      : concept.units.sum,
  )

  const modulesForSum = [
    {
      type: PrebuildType.EINZELARBEIT,
      subType: PrebuildEinzelarbeitSubType.KLASSISCHE_ARBEIT,
    },
    {
      type: PrebuildType.EINZELARBEIT,
      subType: PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR,
    },
    {
      type: PrebuildType.EINZELARBEIT,
      subType: PrebuildEinzelarbeitSubType.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR,
      hybridSize: ModuleSizes.XS,
    },
    {
      type: PrebuildType.MEETING_GROESSE_BESPRECHUNG,
      subType: PrebuildMeetingGroesseBesprechungSubType.PERSON_2,
      hybridSize: ModuleSizes.XS,
    },
    {
      type: PrebuildType.MEETING_GROESSE_BESPRECHUNG,
      subType: PrebuildMeetingGroesseBesprechungSubType.PERSON_3_4,
      hybridSize: ModuleSizes.S,
    },
    {
      type: PrebuildType.MEETING_GROESSE_BESPRECHUNG,
      subType: PrebuildMeetingGroesseBesprechungSubType.PERSON_5_8,
      hybridSize: ModuleSizes.M,
    },
    {
      type: PrebuildType.MEETING_GROESSE_BESPRECHUNG,
      subType: PrebuildMeetingGroesseBesprechungSubType.PERSON_9_12,
      hybridSize: ModuleSizes.L,
    },
    {
      type: PrebuildType.MEETING_GROESSE_BESPRECHUNG,
      subType: PrebuildMeetingGroesseBesprechungSubType.PERSON_13_16,
      hybridSize: ModuleSizes.XL,
    },
    {
      type: PrebuildType.MEETING_GROESSE_COWORKING,
      subType: PrebuildMeetingGroesseCoworkingSubType.PERSON_2_4,
      hybridSize: ModuleSizes.S,
    },
    {
      type: PrebuildType.MEETING_GROESSE_COWORKING,
      subType: PrebuildMeetingGroesseCoworkingSubType.PERSON_5_8,
      hybridSize: ModuleSizes.M,
    },
    {
      type: PrebuildType.MEETING_GROESSE_COWORKING,
      subType: PrebuildMeetingGroesseCoworkingSubType.PERSON_9_12,
      hybridSize: ModuleSizes.L,
    },
    {
      type: PrebuildType.MEETING_GROESSE_COWORKING,
      subType: PrebuildMeetingGroesseCoworkingSubType.PERSON_13_16,
      hybridSize: ModuleSizes.XL,
    },
  ]

  const getTotalAmount = () => {
    const totalStandard = modulesForSum.reduce((acc, n) => (acc += sums.source[n.type][n.subType].practicalSpaceAmount), 0)
    const totalHybrid = Object.values(ModuleSizes).reduce((acc, n) => (acc += sums.source.HYBRID[n].practicalSpaceAmount), 0)
    return totalStandard + totalHybrid
  }

  const getTotalArbeitsplaetze = () => {
    const total = modulesForSum.reduce((acc, n) => {
      let total = sums.source[n.type][n.subType].arbeitsplatzAmount
      if (n.hybridSize) {
        total += sums.source.HYBRID[n.hybridSize].arbeitsplatzAmount
      }
      return acc + total
    }, 0)
    return total
  }

  const getTotalQM = () => {
    const total = modulesForSum.reduce((acc, n) => {
      let total = sums.source[n.type][n.subType].practicalQM
      if (n.hybridSize) {
        total += sums.source.HYBRID[n.hybridSize].practicalQM
      }
      return acc + total
    }, 0)
    return total
  }

  return (
    <div>
      <div className="border-b border-blue font-bold">{t.survey.analysis.concept.summary.modules.title}</div>
      <Composer
        size={ModuleSizes.XS}
        qmSize={genericQMValues.einzelarbeitQM.KLASSISCHE_ARBEIT.value}
        titleSuffix={t.survey.analysis.calculation.values.arbeitsplatz.headline}
        values={[
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsplaetze.KLASSISCHE_ARBEIT,
            ...sums.source.EINZELARBEIT.KLASSISCHE_ARBEIT,
          },
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsplaetze.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR,
            ...sums.source.EINZELARBEIT.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR,
          },
        ]}
      />
      <Composer
        size={ModuleSizes.XS}
        qmSize={genericQMValues.einzelarbeitQM.KLASSISCHE_ARBEIT.value}
        titleSuffix={t.survey.analysis.calculation.values.arbeitsort.headline}
        values={[
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsplaetze.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR,
            ...sums.source.EINZELARBEIT.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR,
          },
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.KOMMUNIKATION_PERSON_2,
            ...sums.source.MEETING_GROESSE_BESPRECHUNG.PERSON_2,
          },
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.HYBRID_PERSON_XS,
            ...sums.source.HYBRID.XS,
          },
        ]}
      />
      <Composer
        size={ModuleSizes.S}
        qmSize={genericQMValues.meetingGroesseBesprechungQM.PERSON_3_4.value}
        titleSuffix={t.survey.analysis.calculation.values.arbeitsort.headline}
        values={[
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.KOMMUNIKATION_PERSON_3_4,
            ...sums.source.MEETING_GROESSE_BESPRECHUNG.PERSON_3_4,
          },
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.KOLLABORATION_PERSON_2_4,
            ...sums.source.MEETING_GROESSE_COWORKING.PERSON_2_4,
          },
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.HYBRID_PERSON_S,
            ...sums.source.HYBRID.S,
          },
        ]}
      />
      <Composer
        size={ModuleSizes.M}
        qmSize={genericQMValues.meetingGroesseBesprechungQM.PERSON_5_8.value}
        titleSuffix={t.survey.analysis.calculation.values.arbeitsort.headline}
        values={[
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.KOMMUNIKATION_PERSON_5_8,
            ...sums.source.MEETING_GROESSE_BESPRECHUNG.PERSON_5_8,
          },
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.KOLLABORATION_PERSON_5_8,
            ...sums.source.MEETING_GROESSE_COWORKING.PERSON_5_8,
          },
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.HYBRID_PERSON_M,
            ...sums.source.HYBRID.M,
          },
        ]}
      />
      <Composer
        size={ModuleSizes.L}
        qmSize={genericQMValues.meetingGroesseBesprechungQM.PERSON_9_12.value}
        titleSuffix={t.survey.analysis.calculation.values.arbeitsort.headline}
        values={[
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.KOMMUNIKATION_PERSON_9_12,
            ...sums.source.MEETING_GROESSE_BESPRECHUNG.PERSON_9_12,
          },
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.KOLLABORATION_PERSON_9_12,
            ...sums.source.MEETING_GROESSE_COWORKING.PERSON_9_12,
          },
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.HYBRID_PERSON_L,
            ...sums.source.HYBRID.L,
          },
        ]}
      />
      <Composer
        size={ModuleSizes.XL}
        qmSize={genericQMValues.meetingGroesseBesprechungQM.PERSON_13_16.value}
        titleSuffix={t.survey.analysis.calculation.values.arbeitsort.headline}
        values={[
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.KOMMUNIKATION_PERSON_13_16,
            ...sums.source.MEETING_GROESSE_BESPRECHUNG.PERSON_13_16,
          },
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.KOLLABORATION_PERSON_13_16,
            ...sums.source.MEETING_GROESSE_COWORKING.PERSON_13_16,
          },
          {
            label: t.survey.analysis.concept.summary.areaOverview.arbeitsorte.HYBRID_PERSON_XL,
            ...sums.source.HYBRID.XL,
          },
        ]}
      />
      <Item
        bold
        title={t.survey.analysis.concept.summary.areaOverview.total}
        value={getTotalAmount()}
        suffix={`${t.generic.amountSuffix} - ${getTotalArbeitsplaetze()}${
          t.survey.analysis.concept.summary.modules.arbeitsplaetzeSuffix
        } - ${formatNumber(getTotalQM())}${t.generic.squareMeterSuffix}`}
      />
    </div>
  )
}

export default Modules
